// Per spec/blocks.md §spacer. **P-037.30 sixth post-Phase-Close §B**:
// META-category Block that emits a transparent inter-Block spacer of a
// user-configurable height (30 / 60 / 90 px). Composed by chapter staff
// when the default system rhythm (CONTENT→CONTENT = 30 px) isn't enough
// breathing room — e.g., to set off a hero IMG from the section below
// without dropping a `section-heading` Block in between.
//
// **Spacer ADDS to the surrounding system gaps.** This Block carries the
// META padding override `{top: 0, bottom: 0}` (see
// `src/render/block-padding.ts` → META_PADDING_OVERRIDES), so the Block
// itself contributes 0 px to the inter-Block spacer pass on either
// side. The surrounding Blocks still contribute their normal padding.
// Worked example:
//
//   Content → Spacer(30) → Content
//   = 30 (content.bottom)
//   + 0  (spacer.top  via META_PADDING_OVERRIDES)
//   + 30 (spacer rendered content)
//   + 0  (spacer.bottom via META_PADDING_OVERRIDES)
//   + 0  (content.top)
//   = 60 px total visible gap
//
// The `{top: 0, bottom: 0}` override is what makes the user's 30/60/90
// pick read as "ADD this many pixels" rather than "set the gap to this
// many pixels exactly." Without the override, the META default (top: 0,
// bottom: 30) would silently inflate every spacer by 30 px relative to
// the picker label — confusing.
//
// **Render shape** mirrors the existing inline `spacerRow` helper in
// `src/render/render-email.ts` byte-for-byte: single-row standalone
// `<table>` carrying the Outlook-safety attributes (cellpadding /
// cellspacing / border 0, role="presentation", width="100%") and the
// `<td>` with literal `height=N` HTML attr + matching CSS
// `height: Npx; line-height: 0; font-size: 0; mso-line-height-rule:
// exactly;` + `&nbsp;` filler (the `&nbsp;` prevents Outlook from
// collapsing the empty cell; `line-height: 0` + `font-size: 0` collapse
// the `&nbsp;` glyph height to nothing; `mso-line-height-rule: exactly`
// forces Outlook desktop's Word renderer to honor the 0 line-height
// rather than applying its default minimum — P-048.1 D-144 Item 5).

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'

const optionsSchema = z.object({
  // String enum (not numeric) so the Zod `.default('30')` returns a
  // value that round-trips through `JSON.stringify` / form-field state
  // without coercion. The render fn coerces to `Number(...)` at the
  // single emission site below.
  space: z.enum(['30', '60', '90']).default('30'),
})

const inputsSchema = z.object({})

export const spacer = defineBlock({
  id: 'spacer',
  label: 'Spacer',
  category: 'meta',
  optionsSchema,
  inputsSchema,
  defaultOptions: { space: '30' },
  defaultInputs: {},
  render: ({ options }) => {
    const space = Number(options.space)
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td height="${space}" style="height: ${space}px; line-height: 0; font-size: 0; mso-line-height-rule: exactly;">&nbsp;</td></tr></table>`
  },
})
